<template>
    <div class="bk" :style="{ '--bk-cookies': cookiesAlto + 'px' }">
        <!-- ── Lateral: título, texto y la navegación de la cuenta ── -->
        <aside class="bk-side">
            <h1 class="bk-title">
                {{ $t('editar_perfil.title_a') }} <span class="bk-grad">{{ $t('editar_perfil.title_b') }}</span>
            </h1>
            <p class="bk-subtitle">{{ $t('editar_perfil.subtitle') }}</p>
            <CuentaNav />
        </aside>

        <div class="bk-main">
            <form class="bk-form" novalidate @submit.prevent="guardar">

                <!-- 1 · Información personal -->
                <section class="bk-card" :class="{ 'is-done': seccionCompleta(SECCIONES[0]) }" aria-labelledby="ep-s1">
                    <header class="bk-card-head">
                        <span class="bk-step" aria-hidden="true">{{ seccionCompleta(SECCIONES[0]) ? '✓' : '1' }}</span>
                        <div>
                            <h2 id="ep-s1" class="bk-card-title">{{ $t('editar_perfil.s1.title') }}</h2>
                            <p class="bk-card-desc">{{ $t('editar_perfil.s1.desc') }}</p>
                        </div>
                    </header>

                    <!-- Foto de perfil (antes estaba arriba del título del lateral) -->
                    <div class="bk-photo">
                        <button
                            type="button"
                            :class="['bk-avatar-btn', { 'is-uploading': subiendoFoto }]"
                            :disabled="subiendoFoto"
                            :aria-label="fotoUrl ? $t('editar_perfil.photo.change') : $t('editar_perfil.photo.upload')"
                            @click="$refs.foto.click()"
                        >
                            <img v-if="fotoUrl" :src="fotoUrl" :alt="$t('editar_perfil.photo.alt')" class="bk-avatar-img" @error="fotoRota = user && user.imagen" />
                            <span v-else class="bk-avatar-ini" aria-hidden="true">{{ iniciales }}</span>
                            <span class="bk-avatar-overlay" aria-hidden="true">
                                <span v-if="subiendoFoto" class="bk-spinner"></span>
                                <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" /><circle cx="12" cy="13" r="3.5" /></svg>
                            </span>
                        </button>
                        <input ref="foto" type="file" accept="image/*" class="bk-sr-only" tabindex="-1" aria-hidden="true" @change="subirFoto" />
                        <div class="bk-photo-txt">
                            <p class="bk-avatar-name">{{ nombreCompleto }}</p>
                            <button type="button" class="bk-avatar-link" :disabled="subiendoFoto" @click="$refs.foto.click()">
                                {{ subiendoFoto ? $t('editar_perfil.photo.uploading') : (fotoUrl ? $t('editar_perfil.photo.change') : $t('editar_perfil.photo.upload')) }}
                            </button>
                            <p class="bk-avatar-hint">{{ $t('editar_perfil.photo.hint') }}</p>
                        </div>
                    </div>

                    <BkField id="ep-correo" :value="form.correo" :label="$t('editar_perfil.f.email')" type="email"
                        :hint="$t('editar_perfil.f.email_hint')" disabled />

                    <div class="bk-grid-2">
                        <BkField id="ep-nombre" v-model="form.nombre" :label="$t('editar_perfil.f.first_name')" autocomplete="given-name"
                            :maxlength="45" :error="errorDe('nombre')" :ok="okDe('nombre')" @blur="tocar('nombre')" />
                        <BkField id="ep-apellido" v-model="form.apellido" :label="$t('editar_perfil.f.last_name')" autocomplete="family-name"
                            :maxlength="45" :error="errorDe('apellido')" :ok="okDe('apellido')" @blur="tocar('apellido')" />
                    </div>

                    <BkField id="ep-fecha_nacimiento" v-model="form.fecha_nacimiento" :label="$t('editar_perfil.f.birthdate')" type="date"
                        autocomplete="bday" fijo :error="errorDe('fecha_nacimiento')" :ok="okDe('fecha_nacimiento')" @blur="tocar('fecha_nacimiento')" />

                    <!-- Nacionalidad: combo con banderas y buscador -->
                    <div id="ep-pais_idpais" :class="['bk-combo-full', { 'is-invalid': !!errorDe('pais_idpais') }]">
                        <NfCombo
                            v-model="form.pais_idpais"
                            :options="opcionesNacionalidad"
                            :label="$t('editar_perfil.f.nationality')"
                            :search-placeholder="$t('editar_perfil.f.nationality_search')"
                            :empty-text="$t('editar_perfil.f.nationality_empty')"
                            @change="tocar('pais_idpais')"
                        />
                        <p v-if="errorDe('pais_idpais')" class="bk-choice-error" role="alert">{{ errorDe('pais_idpais') }}</p>
                    </div>

                    <!-- Sexo: opciones tipo píldora -->
                    <fieldset id="ep-sexo" :class="['bk-choice', { 'is-invalid': !!errorDe('sexo') }]">
                        <legend class="bk-choice-label">{{ $t('editar_perfil.f.gender') }}</legend>
                        <div class="bk-chips">
                            <label v-for="op in SEXOS" :key="op" class="bk-chip">
                                <input v-model="form.sexo" type="radio" name="ep-sexo" :value="op" @change="tocar('sexo')" />
                                <span>{{ $t(`editar_perfil.gender.${op}`) }}</span>
                            </label>
                        </div>
                        <p v-if="errorDe('sexo')" class="bk-choice-error" role="alert">{{ errorDe('sexo') }}</p>
                    </fieldset>
                </section>

                <!-- 2 · Contacto -->
                <section class="bk-card" :class="{ 'is-done': seccionCompleta(SECCIONES[1]) }" aria-labelledby="ep-s2">
                    <header class="bk-card-head">
                        <span class="bk-step" aria-hidden="true">{{ seccionCompleta(SECCIONES[1]) ? '✓' : '2' }}</span>
                        <div>
                            <h2 id="ep-s2" class="bk-card-title">{{ $t('editar_perfil.s2.title') }}</h2>
                            <p class="bk-card-desc">{{ $t('editar_perfil.s2.desc') }}</p>
                        </div>
                    </header>

                    <div :class="['bk-phone', { 'is-invalid': !!errorDe('telefono') }]">
                        <NfCombo
                            v-model="form.telPais"
                            class="bk-combo"
                            :options="opcionesPrefijo"
                            :label="$t('editar_perfil.f.country')"
                            :search-placeholder="$t('editar_perfil.f.country_search')"
                            :empty-text="$t('editar_perfil.f.country_empty')"
                            show-meta
                            @change="tocar('telefono')"
                        />
                        <BkField id="ep-telefono" :value="form.telNumero" :label="$t('editar_perfil.f.phone')" type="tel" inputmode="numeric"
                            autocomplete="tel-national" :maxlength="20" :error="errorDe('telefono')" :ok="okDe('telefono')"
                            @input="v => { form.telNumero = (v || '').replace(/\D/g, '') }" @blur="tocar('telefono')" />
                    </div>

                    <!-- Cambio de número: se verifica por WhatsApp antes de reemplazar al
                         actual. Si se cierra la página, se va a otra sección o se corta
                         internet, el cambio se cancela y queda el número anterior. -->
                    <div v-if="telCambiado || tel.fase !== 'idle'" class="bk-verify" :class="`is-${tel.fase}`" aria-live="polite">
                        <template v-if="tel.fase === 'idle' || tel.fase === 'enviando'">
                            <p class="bk-verify-text">{{ $t('editar_perfil.tel.keep') }}</p>
                            <p v-if="tel.error" class="bk-choice-error" role="alert">{{ tel.error }}</p>
                            <div class="bk-verify-actions">
                                <button type="button" class="bk-btn bk-btn--primary" :disabled="tel.fase === 'enviando' || !!errores.telefono" @click="iniciarCambioTel">
                                    <span v-if="tel.fase === 'enviando'" class="bk-spinner" aria-hidden="true"></span>
                                    {{ tel.fase === 'enviando' ? $t('editar_perfil.tel.sending') : $t('editar_perfil.tel.verify') }}
                                </button>
                                <button type="button" class="bk-btn bk-btn--ghost" :disabled="tel.fase === 'enviando'" @click="cancelarCambioTel()">
                                    {{ $t('editar_perfil.tel.cancel') }}
                                </button>
                            </div>
                        </template>

                        <template v-else>
                            <p class="bk-verify-text">{{ $t('editar_perfil.tel.sent', { tel: tel.destino }) }}</p>
                            <p v-if="tel.simulado" class="bk-verify-sim">{{ $t('editar_perfil.tel.simulated') }}</p>
                            <div class="bk-verify-row">
                                <BkField id="ep-codigo" :value="tel.codigo" :label="$t('editar_perfil.tel.code')" inputmode="numeric"
                                    autocomplete="one-time-code" :maxlength="6" :error="tel.error"
                                    @input="v => { tel.codigo = (v || '').replace(/\D/g, '').slice(0, 6); tel.error = ''; if (tel.codigo.length === 6) verificarCambioTel() }" />
                                <span class="bk-verify-timer" :class="{ 'is-low': tel.restante <= 60 }">{{ $t('editar_perfil.tel.expires', { t: tiempoRestante }) }}</span>
                            </div>
                            <div class="bk-verify-actions">
                                <button type="button" class="bk-btn bk-btn--primary" :disabled="tel.fase === 'verificando' || tel.codigo.length !== 6" @click="verificarCambioTel">
                                    <span v-if="tel.fase === 'verificando'" class="bk-spinner" aria-hidden="true"></span>
                                    {{ tel.fase === 'verificando' ? $t('editar_perfil.tel.verifying') : $t('editar_perfil.tel.verify') }}
                                </button>
                                <button type="button" class="bk-btn bk-btn--ghost" :disabled="tel.fase === 'verificando' || tel.reenviarEn > 0" @click="reenviarCambioTel">
                                    {{ tel.reenviarEn > 0 ? $t('editar_perfil.tel.resend_in', { s: tel.reenviarEn }) : $t('editar_perfil.tel.resend') }}
                                </button>
                                <button type="button" class="bk-verify-link" :disabled="tel.fase === 'verificando'" @click="cancelarCambioTel()">
                                    {{ $t('editar_perfil.tel.cancel') }}
                                </button>
                            </div>
                        </template>
                    </div>
                </section>

                <!-- 3 · Sobre ti -->
                <section class="bk-card" :class="{ 'is-done': !!form.biografia }" aria-labelledby="ep-s3">
                    <header class="bk-card-head">
                        <span class="bk-step" aria-hidden="true">{{ form.biografia ? '✓' : '3' }}</span>
                        <div>
                            <h2 id="ep-s3" class="bk-card-title">{{ $t('editar_perfil.s3.title') }}</h2>
                            <p class="bk-card-desc">{{ $t('editar_perfil.s3.desc') }}</p>
                        </div>
                    </header>

                    <BkField id="ep-biografia" v-model="form.biografia" :label="$t('editar_perfil.f.bio')" :hint="$t('editar_perfil.f.bio_hint')"
                        optional :optional-text="$t('editar_perfil.optional')" textarea :maxlength="1000" />
                </section>

                <!-- ── Barra de guardado (fija abajo) ── -->
                <div class="bk-bar">
                    <!-- Aviso (éxito / error): sale justo arriba de la barra, alineado con el formulario. -->
                    <transition name="bk-toast">
                        <div
                            v-if="aviso"
                            :key="aviso.id"
                            :class="['bk-toast', `bk-toast--${aviso.tipo}`]"
                            :role="aviso.tipo === 'error' ? 'alert' : 'status'"
                            :aria-live="aviso.tipo === 'error' ? 'assertive' : 'polite'"
                        >
                            <span class="bk-toast-icon" aria-hidden="true">{{ aviso.tipo === 'ok' ? '✓' : '!' }}</span>
                            <p class="bk-toast-text">{{ aviso.texto }}</p>
                            <button v-if="aviso.reintentar" type="button" class="bk-toast-retry" @click="aviso.reintentar">
                                {{ $t('editar_perfil.toast.retry') }}
                            </button>
                            <button type="button" class="bk-toast-close" :aria-label="$t('editar_perfil.toast.close')" @click="cerrarAviso">×</button>
                        </div>
                    </transition>
                    <p class="bk-bar-state" :class="{ 'is-dirty': hayCambios }" aria-live="polite">
                        <span class="bk-bar-dot" aria-hidden="true"></span>
                        {{ hayCambios ? $t('editar_perfil.state_unsaved') : $t('editar_perfil.state_saved') }}
                    </p>
                    <div class="bk-bar-actions">
                        <button v-if="hayCambios" type="button" class="bk-btn bk-btn--ghost" :disabled="guardando" @click="descartar">
                            {{ $t('editar_perfil.discard') }}
                        </button>
                        <button type="submit" class="bk-btn bk-btn--primary" :disabled="guardando || !hayCambios">
                            <span v-if="guardando" class="bk-spinner" aria-hidden="true"></span>
                            {{ guardando ? $t('editar_perfil.saving') : $t('editar_perfil.save') }}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import axios from "axios";
import { redirectToSavedUrl } from "@/Utils/Utils";
import apiUrls from "@/new-constants/apiUrls";
import { PAISES_TELEFONO, nombrePais, separarTelefono } from "@/assets/data/paisesTelefono";
import NfCombo from "@/components/Formulario/NfCombo.vue";
import BkField from "@/components/BankInformation/BkField.vue";
import cuentaFormMixin from "@/components/BankInformation/cuentaFormMixin";
import CuentaNav from "@/components/Cuenta/CuentaNav.vue";

// Los campos que se editan. El teléfono se edita en dos partes (país +
// número) y se guarda junto y SIN "+", como siempre: "5491122334455".
const CAMPOS = ['nombre', 'apellido', 'fecha_nacimiento', 'pais_idpais', 'sexo', 'telPais', 'telNumero', 'biografia'];
// Los que se guardan con "Guardar perfil" (el teléfono va por su verificación).
const CAMPOS_PERFIL = CAMPOS.filter(c => c !== 'telPais' && c !== 'telNumero');
// Error de la API (motivo) → texto del aviso de la verificación.
const MOTIVO_TEL = {
    ya_existe: 'exists', no_existe: 'no_whatsapp', sin_respuesta: 'error', sin_envio: 'no_send',
    invalido: 'invalid', igual: 'invalid', incorrecto: 'incorrect', demasiados_intentos: 'too_many',
    vencido: 'expired', sin_cambio: 'expired', espera: 'wait',
};
// Obligatorios (los mismos que pide la API en users/update).
const OBLIGATORIOS = ['nombre', 'apellido', 'fecha_nacimiento', 'pais_idpais', 'sexo', 'telefono'];
const SECCIONES = [
    ['nombre', 'apellido', 'fecha_nacimiento', 'pais_idpais', 'sexo'],
    ['telefono'],
];
// m masculino, f femenino, b no binario, n prefiero no decirlo (enum de usuario.sexo).
const SEXOS = ['m', 'f', 'b', 'n'];
const FOTO_MAX_BYTES = 5 * 1024 * 1024;
// La tabla `pais` tiene "DS" para Samoa Americana: el código ISO es "AS".
const ISO_CORREGIDO = { DS: 'AS' };

const vacio = v => v === null || v === undefined || String(v).trim() === '';

export default {
    name: "EditarPerfil",
    components: { NfCombo, BkField, CuentaNav },
    mixins: [cuentaFormMixin],
    data() {
        return {
            OBLIGATORIOS,
            SECCIONES,
            SEXOS,
            // El perfil completo como está en el navegador: lo que no se edita
            // acá (correo, imagen, ids…) viaja igual que antes.
            perfil: JSON.parse(localStorage.getItem('usuario') || '{}') || {},
            form: this.formVacio(),
            guardado: this.formVacio(),
            paises: [],
            cargando: false,
            guardando: false,
            intentoGuardar: false,
            tocados: {},
            subiendoFoto: false,
            fotoRota: null,
            s3Url: process.env.VUE_APP_S3_URL,
            // Cambio de teléfono en curso. `sesion` es la clave que da la API y
            // vive SOLO en memoria: si se recarga, se cierra o se sale de la
            // página, se pierde y el cambio ya no se puede completar.
            tel: { fase: 'idle', sesion: null, destino: '', simulado: false, codigo: '', error: '', restante: 0, reenviarEn: 0 },
            telTimer: null,
        };
    },
    computed: {
        ...mapState(["user"]),

        fotoUrl() {
            const img = this.user && this.user.imagen;
            // Si la imagen no carga (borrada en S3), se muestran las iniciales.
            if (this.fotoRota === img) return null;
            return img && img !== 'null' && img !== 'undefined' ? `${this.s3Url}perfil/${img}` : null;
        },
        nombreCompleto() {
            return [this.form.nombre, this.form.apellido].filter(Boolean).join(' ') || this.form.correo;
        },
        iniciales() {
            const ini = [this.form.nombre, this.form.apellido].filter(Boolean).map(t => t.trim().charAt(0)).join('');
            return (ini || (this.form.correo || 'U').charAt(0)).toUpperCase();
        },

        // Nacionalidad: los países de la tabla `pais` (los que acepta la API),
        // con bandera y nombre en el idioma del sitio.
        opcionesNacionalidad() {
            const locale = this.$i18n.locale;
            return this.paises
                .map(p => {
                    const iso = ISO_CORREGIDO[p.codigo] || p.codigo || '';
                    return { value: String(p.idpais), label: iso ? nombrePais(iso, locale) : p.nombre, flag: iso };
                })
                .sort((a, b) => a.label.localeCompare(b.label, locale));
        },
        // Prefijo: todos los países con código telefónico (paisesTelefono.js).
        opcionesPrefijo() {
            const locale = this.$i18n.locale;
            return PAISES_TELEFONO
                .map(p => ({ value: p.code, label: nombrePais(p.code, locale), meta: '+' + p.dial, flag: p.code }))
                .sort((a, b) => a.label.localeCompare(b.label, locale));
        },

        // Teléfono como lo guarda la API: prefijo + número, solo dígitos.
        telefono() {
            const pais = PAISES_TELEFONO.find(p => p.code === this.form.telPais);
            return pais && this.form.telNumero ? `${pais.dial}${this.form.telNumero}` : '';
        },

        errores() {
            const f = this.form;
            const e = {};
            OBLIGATORIOS.forEach(c => {
                const valor = c === 'telefono' ? (f.telPais && f.telNumero) : f[c];
                if (vacio(valor)) e[c] = this.$t('validations.required');
            });
            if (!e.telefono && f.telNumero.length < 6) e.telefono = this.$t('validations.min_length', { min: 6 });
            return e;
        },
        hayCambios() {
            // El teléfono no cuenta: se cambia aparte, verificándolo por WhatsApp.
            return CAMPOS_PERFIL.some(c => (this.form[c] || '') !== (this.guardado[c] || ''));
        },
        telCambiado() {
            return this.form.telPais !== this.guardado.telPais || this.form.telNumero !== this.guardado.telNumero;
        },
        tiempoRestante() {
            const s = Math.max(0, this.tel.restante);
            return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
        },
        // Teléfono guardado (el actual), en el formato de la API.
        telefonoGuardado() {
            const pais = PAISES_TELEFONO.find(p => p.code === this.guardado.telPais);
            return pais && this.guardado.telNumero ? `${pais.dial}${this.guardado.telNumero}` : (this.perfil.telefono || '');
        },
    },
    watch: {
        // "Revisa los campos marcados en rojo" se va solo al corregirlos.
        errores(e) {
            if (!Object.keys(e).length && this.aviso && this.aviso.validacion) this.cerrarAviso();
        },
        // Si edita el número con el código ya pedido, ese código era para otro
        // número: se cancela y hay que volver a pedirlo.
        'form.telPais'() { this.alEditarTelefono(); },
        'form.telNumero'() { this.alEditarTelefono(); },
    },
    methods: {
        ...mapActions(["actualizarPerfil"]),

        formVacio() {
            return { correo: '', nombre: '', apellido: '', fecha_nacimiento: '', pais_idpais: '', sexo: '', telPais: '', telNumero: '', biografia: '' };
        },

        // Perfil (como viene del navegador / la API) → formulario.
        desdePerfil(p) {
            const tel = String(p.telefono || '').replace(/[^\d+]/g, '');
            const { pais, numero } = separarTelefono(tel ? (tel[0] === '+' ? tel : '+' + tel) : '');
            const sexo = String(p.sexo || '').toLowerCase();
            return {
                correo: p.correo || '',
                nombre: p.nombre || '',
                apellido: p.apellido || '',
                fecha_nacimiento: p.fecha_nacimiento ? String(p.fecha_nacimiento).slice(0, 10) : '',
                pais_idpais: p.pais_idpais != null && p.pais_idpais !== '' ? String(p.pais_idpais) : '',
                sexo: SEXOS.includes(sexo) ? sexo : '',
                telPais: pais,
                telNumero: pais ? numero.replace(/\D/g, '') : tel.replace(/\D/g, ''),
                biografia: p.biografia && p.biografia !== 'null' ? String(p.biografia) : '',
            };
        },

        // ── Validación ────────────────────────────────────────────────────
        tocar(campo) {
            this.$set(this.tocados, campo, true);
        },
        // El error se muestra al salir del campo o al intentar guardar.
        errorDe(campo) {
            return (this.tocados[campo] || this.intentoGuardar) && this.errores[campo] ? this.errores[campo] : '';
        },
        okDe(campo) {
            const valor = campo === 'telefono' ? this.form.telNumero : this.form[campo];
            return !vacio(valor) && !this.errores[campo];
        },
        seccionCompleta(campos) {
            return campos.every(c => !this.errores[c]);
        },
        enfocarPrimerError() {
            const campo = OBLIGATORIOS.find(c => this.errores[c]);
            const cont = campo && document.getElementById(`ep-${campo}`);
            if (!cont) return;
            cont.scrollIntoView({ behavior: 'smooth', block: 'center' });
            const control = cont.matches('input, textarea, button') ? cont : cont.querySelector('input, button');
            if (control) setTimeout(() => control.focus({ preventScroll: true }), 350);
        },

        // ── Guardar ───────────────────────────────────────────────────────
        async guardar() {
            if (this.guardando) return;
            this.intentoGuardar = true;
            if (Object.keys(this.errores).length) {
                this.mostrarAviso('error', this.$t('editar_perfil.toast.invalid'), null, { validacion: true });
                this.$nextTick(this.enfocarPrimerError);
                return;
            }

            this.guardando = true;
            try {
                const payload = {
                    ...this.perfil,
                    nombre: this.form.nombre.trim(),
                    apellido: this.form.apellido.trim(),
                    fecha_nacimiento: this.form.fecha_nacimiento,
                    pais_idpais: this.form.pais_idpais,
                    sexo: this.form.sexo,
                    // El teléfono que se manda es el ACTUAL: uno nuevo se cambia
                    // solo verificándolo por WhatsApp (la API también lo exige).
                    telefono: this.telefonoGuardado,
                    biografia: this.form.biografia.trim(),
                };
                await this.actualizarPerfil(payload);

                this.perfil = { ...this.perfil, ...payload };
                this.form = { ...this.form, nombre: payload.nombre, apellido: payload.apellido, biografia: payload.biografia };
                CAMPOS_PERFIL.forEach(c => { this.guardado[c] = this.form[c]; });
                this.intentoGuardar = false;
                this.tocados = {};
                this.mostrarAviso('ok', this.$t('editar_perfil.toast.saved'));

                // Si llegó acá desde otra pantalla que le pidió completar el
                // perfil (p. ej. antes de reservar), sigue a donde iba. Si no,
                // se queda en la página.
                if (localStorage.getItem('redirect')) {
                    setTimeout(() => redirectToSavedUrl(this.$router), 1800);
                }
            } catch (error) {
                console.error('[EditarPerfil] error guardando el perfil:', error);
                this.mostrarAviso('error', this.$t('editar_perfil.toast.error_save'), () => { this.cerrarAviso(); this.guardar(); });
            } finally {
                this.guardando = false;
            }
        },

        descartar() {
            this.cancelarCambioTel(null, { avisar: false });
            this.form = { ...this.guardado };
            this.intentoGuardar = false;
            this.tocados = {};
        },

        // ── Cambio de teléfono (WhatsApp) ─────────────────────────────────
        auth() {
            return { headers: { Authorization: localStorage.getItem('token') || '' } };
        },
        textoTel(data, error) {
            if (error && !error.response) return this.$t('editar_perfil.tel.offline');
            const motivo = data && data.motivo;
            const clave = MOTIVO_TEL[motivo] || 'error';
            return this.$t(`editar_perfil.tel.${clave}`, {
                n: data && data.intentos_restantes,
                s: data && data.segundos_restantes,
            });
        },
        arrancarReloj(vigencia, espera) {
            this.pararReloj();
            const vence = Date.now() + vigencia * 1000;
            const reenviar = Date.now() + espera * 1000;
            const tick = () => {
                this.tel.restante = Math.round((vence - Date.now()) / 1000);
                this.tel.reenviarEn = Math.max(0, Math.round((reenviar - Date.now()) / 1000));
                if (this.tel.restante <= 0) this.cancelarCambioTel('expired');
            };
            tick();
            this.telTimer = setInterval(tick, 1000);
        },
        pararReloj() {
            clearInterval(this.telTimer);
            this.telTimer = null;
        },

        async iniciarCambioTel() {
            if (this.tel.fase !== 'idle' || this.errores.telefono) return;
            this.tel.error = '';
            this.tel.fase = 'enviando';
            try {
                const { data } = await axios.post(apiUrls.telefonoCambioIniciar, { telefono: this.telefono });
                if (data.pendiente === false) {
                    // Verificación apagada por configuración: ya quedó cambiado.
                    this.aplicarTelefonoNuevo(data.user);
                    return;
                }
                this.tel = { ...this.tel, fase: 'codigo', sesion: data.sesion, destino: data.telefono, simulado: !!data.simulado, codigo: '' };
                this.arrancarReloj(data.vigencia_segundos, data.espera_reenvio);
                this.$nextTick(() => { const el = document.getElementById('ep-codigo'); if (el) el.focus(); });
            } catch (error) {
                this.tel.fase = 'idle';
                this.tel.error = this.textoTel(error.response && error.response.data, error);
            }
        },

        async reenviarCambioTel() {
            if (!this.tel.sesion || this.tel.reenviarEn > 0) return;
            this.tel.error = '';
            try {
                const { data } = await axios.post(apiUrls.telefonoCambioReenviar, { sesion: this.tel.sesion });
                this.tel.codigo = '';
                this.arrancarReloj(data.vigencia_segundos, data.espera_reenvio);
            } catch (error) {
                const data = error.response && error.response.data;
                if (!error.response || (data && (data.motivo === 'vencido' || data.motivo === 'sin_cambio'))) {
                    return this.cancelarCambioTel(!error.response ? 'offline' : 'expired');
                }
                this.tel.error = this.textoTel(data, error);
            }
        },

        async verificarCambioTel() {
            if (this.tel.fase !== 'codigo' || this.tel.codigo.length !== 6) return;
            this.tel.fase = 'verificando';
            this.tel.error = '';
            try {
                const { data } = await axios.post(apiUrls.telefonoCambioVerificar, { sesion: this.tel.sesion, codigo: this.tel.codigo });
                this.aplicarTelefonoNuevo(data.user);
            } catch (error) {
                const data = error.response && error.response.data;
                if (!error.response) return this.cancelarCambioTel('offline');
                if (data && (data.motivo === 'vencido' || data.motivo === 'sin_cambio')) return this.cancelarCambioTel('expired');
                if (data && data.motivo === 'ya_existe') {
                    this.cancelarCambioTel(null, { avisar: false });
                    return this.mostrarAviso('error', this.$t('editar_perfil.tel.exists'));
                }
                this.tel.fase = 'codigo';
                this.tel.codigo = '';
                this.tel.error = this.textoTel(data, error);
            }
        },

        // El número quedó verificado y guardado en la API.
        aplicarTelefonoNuevo(user) {
            this.pararReloj();
            const telefono = (user && user.telefono) || this.telefono;
            this.perfil = { ...this.perfil, telefono };
            localStorage.setItem('usuario', JSON.stringify(this.perfil));
            localStorage.setItem('telefono', telefono);
            this.guardado.telPais = this.form.telPais;
            this.guardado.telNumero = this.form.telNumero;
            this.tel = { fase: 'idle', sesion: null, destino: '', simulado: false, codigo: '', error: '', restante: 0, reenviarEn: 0 };
            this.mostrarAviso('ok', this.$t('editar_perfil.tel.ok'));
        },

        /**
         * Cancela el cambio y vuelve al número anterior. `motivo`: expired,
         * offline o null (lo canceló la persona). Avisa a la API para borrar el
         * pedido; si no llega (sin internet), igual vence solo y la clave de
         * sesión ya no existe en ningún lado.
         */
        cancelarCambioTel(motivo = null, { avisar = true } = {}) {
            const sesion = this.tel.sesion;
            this.pararReloj();
            this.tel = { fase: 'idle', sesion: null, destino: '', simulado: false, codigo: '', error: '', restante: 0, reenviarEn: 0 };
            this.form.telPais = this.guardado.telPais;
            this.form.telNumero = this.guardado.telNumero;
            this.cancelarEnServidor(sesion);
            if (avisar && motivo) this.mostrarAviso('error', this.$t(`editar_perfil.tel.${motivo}`));
        },
        // keepalive: el pedido sale aunque la página se esté cerrando.
        cancelarEnServidor(sesion) {
            if (!sesion) return;
            try {
                fetch(apiUrls.telefonoCambioCancelar, {
                    method: 'POST',
                    keepalive: true,
                    headers: { 'Content-Type': 'application/json', ...this.auth().headers },
                    body: JSON.stringify({ sesion }),
                }).catch(() => {});
            } catch (e) { /* sin red: vence solo */ }
        },
        alEditarTelefono() {
            if (this.tel.fase === 'codigo' || this.tel.fase === 'verificando') this.cancelarCambioTel(null, { avisar: false });
            if (this.tel.error && this.tel.fase === 'idle') this.tel.error = '';
        },
        alQuedarSinRed() {
            if (this.tel.sesion) this.cancelarCambioTel('offline');
        },
        alCerrarPagina() {
            this.cancelarEnServidor(this.tel.sesion);
        },

        // ── Foto ──────────────────────────────────────────────────────────
        async subirFoto(e) {
            const file = e.target.files && e.target.files[0];
            e.target.value = '';
            if (!file) return;
            if (!/^image\//.test(file.type)) return this.mostrarAviso('error', this.$t('editar_perfil.toast.photo_type'));
            if (file.size > FOTO_MAX_BYTES) return this.mostrarAviso('error', this.$t('editar_perfil.toast.photo_size'));

            this.subiendoFoto = true;
            try {
                const datos = new FormData();
                datos.append('foto', file);
                const { data } = await axios.post(`${process.env.VUE_APP_API_URL}users/updatePhoto`, datos);
                this.$store.commit('setUserImage', data.imagen);
                localStorage.setItem('imagen', data.imagen);
                this.mostrarAviso('ok', this.$t('editar_perfil.toast.photo_ok'));
            } catch (error) {
                console.error('[EditarPerfil] error subiendo la foto:', error);
                this.mostrarAviso('error', this.$t('editar_perfil.toast.photo_error'));
            } finally {
                this.subiendoFoto = false;
            }
        },
    },
    created() {
        window.scrollTo(0, 0);
        this.form = this.desdePerfil(this.perfil);
        this.guardado = { ...this.form };

        axios.get(process.env.VUE_APP_API_URL + "countries")
            .then(({ data }) => { this.paises = Array.isArray(data) ? data : []; })
            .catch(error => { console.error('[EditarPerfil] no se pudieron leer los países:', error); });
    },
    mounted() {
        // Sin internet o cerrando la pestaña: el cambio de número se cancela.
        window.addEventListener('offline', this.alQuedarSinRed);
        window.addEventListener('pagehide', this.alCerrarPagina);
    },
    beforeDestroy() {
        // Salir a otra sección: el cambio de número pendiente se cancela.
        window.removeEventListener('offline', this.alQuedarSinRed);
        window.removeEventListener('pagehide', this.alCerrarPagina);
        this.pararReloj();
        this.cancelarEnServidor(this.tel.sesion);
    },
};
</script>

<!-- Estilos compartidos con Datos bancarios (todo con prefijo bk-). -->
<style src="../../components/BankInformation/bkCuenta.css"></style>
<style src="../../components/Cuenta/cuentaMenu.css"></style>
